<template>
  <div class="mall-checkout">
    <div class="mall-checkout__shell">
      <header class="mall-checkout__head">
        <h1>주문하기</h1>
        <p v-if="!checkoutEmpty" class="mall-checkout__steps">
          <strong v-if="step === 'shipping'">1 배송지</strong>
          <span v-else>1 배송지</span>
          <i>›</i>
          <strong v-if="step === 'payment'">2 결제</strong>
          <span v-else>2 결제</span>
        </p>
      </header>

      <div
        v-if="pendingOrder"
        class="mall-checkout__pending"
        :class="{ 'is-confirming': Boolean(pendingOrder.payNotifiedAt) }"
      >
        <AppIcon
          :icon="pendingOrder.payNotifiedAt ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline'"
          :size="18"
        />
        <p>
          <template v-if="pendingOrder.payMethod === 'TOSS'">
            결제를 마치지 않은 주문이 있습니다.
          </template>
          <template v-else-if="pendingOrder.payNotifiedAt">
            입금 확인 중인 주문이 있습니다.
          </template>
          <template v-else>
            입금 대기 중인 주문이 있습니다.
          </template>
          <router-link :to="`/mall/orders/${pendingOrder.code}`">
            {{ pendingOrder.code }} 확인하기
          </router-link>
        </p>
      </div>

      <div v-if="checkoutEmpty" class="mall-checkout__empty">
        <p>주문할 상품이 없습니다.</p>
        <router-link to="/mall/cart">장바구니로 이동</router-link>
      </div>

      <form v-else class="mall-checkout__layout" @submit.prevent="onSubmit">
        <div class="mall-checkout__main">
          <section class="mall-checkout__card">
            <h2>주문 상품 {{ orderLines.length }}</h2>
            <ul class="mall-checkout__items">
              <li v-for="line in orderLines" :key="line.key">
                <div class="mall-checkout__item-thumb">
                  <img :src="line.product.image" :alt="line.product.name" />
                </div>
                <div class="mall-checkout__item-body">
                  <strong>{{ line.product.name }}</strong>
                  <span v-if="line.optionName" class="mall-checkout__item-option">{{ line.optionName }}</span>
                  <span class="mall-checkout__item-price">
                    <b>{{ line.qty }}개</b>
                    <b>{{ formatPrice(line.lineTotal) }}원</b>
                  </span>
                </div>
              </li>
            </ul>
          </section>

          <section v-show="step === 'shipping'" class="mall-checkout__card">
            <div class="mall-checkout__card-head">
              <h2>배송지</h2>
              <div class="mall-checkout__card-actions">
                <router-link :to="MALL_SHIPPING_PATH" class="mall-checkout__text-btn">배송지 관리</router-link>
                <button type="button" class="mall-checkout__text-btn" @click="startNewAddress">
                  새로 입력
                </button>
              </div>
            </div>

            <div
              v-if="addressOptions.length"
              class="mall-checkout__addr-chips"
              aria-label="배송지 바로선택"
            >
              <button
                v-for="option in addressOptions"
                :key="`chip-${option.id}`"
                type="button"
                class="mall-checkout__chip"
                :class="{ 'is-active': selectedAddressId === option.id }"
                @click="selectAddress(option.id)"
              >
                {{ option.label }}
              </button>
            </div>

            <div class="mall-checkout__fields">
              <label>
                <span>수령인</span>
                <input v-model.trim="form.receiverName" required placeholder="이름" />
              </label>
              <label>
                <span>연락처</span>
                <input v-model.trim="form.receiverPhone" required placeholder="010-0000-0000" />
              </label>
              <label class="is-full">
                <span>주소</span>
                <div class="mall-checkout__zip-row">
                  <input
                    :value="form.zipcode"
                    type="text"
                    placeholder="우편번호"
                    readonly
                    @click="onSearchAddress"
                  />
                  <button
                    type="button"
                    class="mall-checkout__ghost"
                    :disabled="postcodeOpening"
                    @click="onSearchAddress"
                  >
                    주소 검색
                  </button>
                </div>
                <input
                  :value="form.address"
                  type="text"
                  required
                  placeholder="주소 검색을 눌러 주세요"
                  readonly
                  class="mall-checkout__addr-input"
                  @click="onSearchAddress"
                />
                <input
                  v-model.trim="form.addressDetail"
                  type="text"
                  maxlength="80"
                  placeholder="상세 주소 (동·호수 등)"
                  class="mall-checkout__addr-input"
                />
              </label>
              <label class="is-full mall-checkout__check">
                <input v-model="saveAddress" type="checkbox" />
                <span>
                  {{
                    isSavedSelection
                      ? '변경한 내용으로 이 배송지를 저장'
                      : '이 배송지를 다음에 쓰기'
                  }}
                </span>
              </label>
              <label class="is-full mall-checkout__check">
                <input v-model="setAsDefault" type="checkbox" />
                <span>기본 배송지로 사용</span>
              </label>
              <label v-if="saveAddress" class="is-full">
                <span>배송지 이름</span>
                <input v-model.trim="form.label" maxlength="40" placeholder="예: 본원 · 분원" />
              </label>
              <label class="is-full">
                <span>이메일 (선택)</span>
                <input
                  v-model.trim="form.email"
                  type="email"
                  placeholder="결제·배송 안내를 받을 이메일"
                />
                <em class="mall-checkout__field-note">적으면 회원 이메일로 저장하고, 결제부터 메일로 안내합니다.</em>
              </label>
              <p class="mall-checkout__field-note mall-checkout__field-note--block">
                위의 연락처는 회원 휴대폰 번호로 저장됩니다.
              </p>

              <label class="is-full">
                <span>배송 요청</span>
                <div class="mall-checkout__memo-presets">
                  <button
                    v-for="preset in MEMO_PRESETS"
                    :key="preset"
                    type="button"
                    class="mall-checkout__chip"
                    :class="{ 'is-active': form.memo === preset }"
                    @click="form.memo = preset"
                  >
                    {{ preset }}
                  </button>
                </div>
                <input
                  v-model.trim="form.memo"
                  maxlength="255"
                  placeholder="필요하면 직접 입력"
                />
              </label>
              <label class="is-full mall-checkout__check">
                <input v-model="saveMemo" type="checkbox" />
                <span>이 요청을 다음 주문 기본값으로 저장</span>
              </label>
            </div>
          </section>

          <section v-show="step === 'payment'" class="mall-checkout__card">
            <h2>결제</h2>
            <div class="mall-checkout__toss">
              <div class="mall-checkout__pay-amount">
                <span>결제할 금액</span>
                <strong>{{ formatPrice(orderPayable) }}원</strong>
              </div>
              <p v-if="!tossEnabled" class="mall-checkout__pay-guide">
                결제창을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.
              </p>
              <template v-else>
                <p v-if="tossPreparing" class="mall-checkout__pay-guide">결제창을 불러오는 중입니다.</p>
                <div id="toss-payment-method" class="mall-checkout__toss-frame"></div>
                <div id="toss-agreement" class="mall-checkout__toss-frame"></div>
                <p class="mall-checkout__pay-caution">
                  카드, 간편결제, 휴대폰결제를 받습니다. 계좌이체·가상계좌를 고르면 그 결제는 취소됩니다.
                </p>
                <p v-if="tossTestMode" class="mall-checkout__pay-test">
                  테스트 결제입니다. 승인해도 카드에서 금액이 빠지지 않습니다.
                </p>
              </template>
            </div>
          </section>
        </div>

        <aside class="mall-checkout__summary">
          <h2>결제 금액</h2>
          <dl>
            <div>
              <dt>상품금액</dt>
              <dd>{{ formatPrice(orderListSubtotal) }}원</dd>
            </div>
            <div>
              <dt>주문 배송비</dt>
              <dd>{{ orderShippingLabel }}</dd>
            </div>
          </dl>
          <div class="mall-checkout__cta">
            <div class="mall-checkout__payable">
              <span>결제할 금액</span>
              <strong>{{ formatPrice(orderPayable) }}원</strong>
            </div>
            <button
              v-if="step === 'shipping'"
              type="button"
              class="mall-checkout__submit"
              :disabled="submitting || !orderLines.length"
              @click="goToPayment"
            >
              {{ submitting ? '저장 중…' : '다음 · 결제하기' }}
            </button>
            <button
              v-else-if="step === 'payment'"
              type="submit"
              class="mall-checkout__submit"
              :disabled="submitting || !orderLines.length || !tossEnabled || !tossReady"
            >
              {{
                submitting
                  ? '주문 접수 중…'
                  : tossReady
                    ? `${formatPrice(orderPayable)}원 결제하기`
                    : '결제창 준비 중…'
              }}
            </button>
          </div>
          <button
            v-if="step === 'payment'"
            type="button"
            class="mall-checkout__text-btn"
            :disabled="submitting"
            @click="step = 'shipping'"
          >
            배송지 다시 보기
          </button>
          <p v-if="errorMessage" class="mall-checkout__error">{{ errorMessage }}</p>
          <router-link
            v-if="placedOrder && step === 'payment'"
            class="mall-checkout__text-btn"
            :to="`/mall/orders/${placedOrder.code}`"
          >
            주문 상세에서 결제 이어하기
          </router-link>
        </aside>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  clearBuyNowKeys,
  getBuyNowKeys,
  isBuyNowCheckoutQuery,
} from '@/composables/useBuyNowCheckout'
import AppIcon from '@/components/common/AppIcon.vue'
import { useCart } from '@/composables/useCart'
import { useCartSelection } from '@/composables/useCartSelection'
import { useDaumPostcode } from '@/composables/useDaumPostcode'
import { useMemberAuth } from '@/composables/useMemberAuth'
import { MALL_SHIPPING_PATH } from '@/constants/mall'
import { formatPrice } from '@/constants/mallProducts'
import {
  createMallOrder,
  fetchMyOrders,
  fetchTossAccess,
  type MallOrder,
} from '@/api/mallOrder'
import {
  createShippingAddress,
  fetchShippingBook,
  saveShippingMemo,
  updateShippingAddress,
  type MallShippingAddress,
} from '@/api/mallShipping'
import { updateMemberContact } from '@/api/member'
import { useTossPayment } from '@/composables/useTossPayment'
import { confirmAction } from '@/utils/confirmAction'
import { formatShippingFeeAmount, toShippingLine } from '@/utils/mallShipping'
import { isEmailValid, isPhoneValid } from '@/utils/formValidation'
import { calcOrderShippingFromLines } from '@/utils/mallSell'

const ORG_ADDRESS_ID = 'org'
const NEW_ADDRESS_ID = 'new'

const MEMO_PRESETS = [
  '배송 전 연락 바랍니다',
  '문 앞에 놓아주세요',
  '경비실에 맡겨주세요',
  '직접 받겠습니다',
] as const

type AddressOption = {
  id: string
  label: string
  receiver: string
  phone: string
  zipcode: string
  address: string
  addressDetail: string
  isDefault: boolean
}

const route = useRoute()
const router = useRouter()
const { lines, reload } = useCart()
const { selectedLines, selectedKeys, clearSelection, selectOnly } = useCartSelection()
const { member, setMember } = useMemberAuth()
const step = ref<'shipping' | 'payment'>('shipping')
const tossEnabled = ref(false)
const {
  ready: tossReady,
  testMode: tossTestMode,
  preparing: tossPreparing,
  mount: mountToss,
  pay: requestTossPay,
} = useTossPayment()
const placedOrder = ref<MallOrder | null>(null)

const buyNowMode = computed(() => isBuyNowCheckoutQuery(route.query))
const buyNowKeys = ref<string[]>(buyNowMode.value ? getBuyNowKeys() : [])

function syncBuyNowSelection() {
  if (!buyNowMode.value) {
    buyNowKeys.value = []
    return
  }
  buyNowKeys.value = getBuyNowKeys()
  if (buyNowKeys.value.length) {
    selectOnly(buyNowKeys.value)
  }
}

/** 바로구매: 상세에서 넘긴 상품만. 장바구니: 고른 줄(없으면 전체). */
const orderLines = computed(() => {
  if (buyNowMode.value) {
    const allow = new Set(buyNowKeys.value)
    return lines.value.filter((line) => allow.has(line.key))
  }
  return selectedKeys.value.length ? selectedLines.value : lines.value
})
const orderSubtotal = computed(() =>
  orderLines.value.reduce((sum, line) => sum + line.lineTotal, 0),
)
const orderListSubtotal = computed(() =>
  orderLines.value.reduce((sum, line) => sum + line.listUnit * line.qty, 0),
)
const orderShipping = computed(() =>
  calcOrderShippingFromLines(
    orderLines.value.map((line) => toShippingLine(line.product, line.qty)),
  ),
)
const orderShippingLabel = computed(() => formatShippingFeeAmount(orderShipping.value))
const orderPayable = computed(() => orderSubtotal.value + orderShipping.value)
const checkoutEmpty = computed(() => !orderLines.value.length && !placedOrder.value)
const { opening: postcodeOpening, searchPostcode } = useDaumPostcode()

const submitting = ref(false)
const errorMessage = ref('')
const pendingOrder = ref<MallOrder | null>(null)
const savedAddresses = ref<MallShippingAddress[]>([])
const selectedAddressId = ref(ORG_ADDRESS_ID)
const saveAddress = ref(false)
const setAsDefault = ref(true)
const saveMemo = ref(false)
const hydrated = ref(false)

const isSavedSelection = computed(() => {
  const id = selectedAddressId.value
  return id !== ORG_ADDRESS_ID && id !== NEW_ADDRESS_ID && /^\d+$/.test(id)
})

const form = reactive({
  label: '',
  receiverName: '',
  receiverPhone: '',
  zipcode: '',
  address: '',
  addressDetail: '',
  memo: '',
  email: '',
})

const orgOption = computed<AddressOption | null>(() => {
  const m = member.value
  if (!m?.address) return null
  return {
    id: ORG_ADDRESS_ID,
    label: '기관 주소',
    receiver: m.bossName || m.orgName || '',
    phone: m.bossPhone || m.orgPhone || '',
    zipcode: m.zipcode || '',
    address: m.address,
    addressDetail: m.addressDetail || '',
    isDefault: savedAddresses.value.length === 0,
  }
})

const addressOptions = computed<AddressOption[]>(() => {
  const saved = savedAddresses.value.map((item) => ({
    id: String(item.idx),
    label: item.label,
    receiver: item.receiver,
    phone: item.phone,
    zipcode: item.zipcode,
    address: item.address,
    addressDetail: item.addressDetail,
    isDefault: item.isDefault,
  }))
  return orgOption.value ? [orgOption.value, ...saved] : saved
})

function applyOption(option: AddressOption) {
  form.receiverName = option.receiver
  form.receiverPhone = option.phone
  form.zipcode = option.zipcode
  form.address = option.address
  form.addressDetail = option.addressDetail
  form.label = option.id === ORG_ADDRESS_ID || option.id === NEW_ADDRESS_ID ? '' : option.label
  saveAddress.value = false
  setAsDefault.value = option.isDefault
}

function selectAddress(id: string) {
  selectedAddressId.value = id
  const option = addressOptions.value.find((item) => item.id === id)
  if (option) applyOption(option)
}

function startNewAddress() {
  selectedAddressId.value = NEW_ADDRESS_ID
  form.label = ''
  form.receiverName = member.value?.bossName || member.value?.orgName || ''
  form.receiverPhone = member.value?.bossPhone || member.value?.orgPhone || ''
  form.zipcode = ''
  form.address = ''
  form.addressDetail = ''
  saveAddress.value = true
  setAsDefault.value = savedAddresses.value.length === 0
}

function fillMemberDefaults(force = false) {
  if (!member.value) return
  if (force || !form.email) form.email = member.value.bossEmail || ''
}

async function loadShippingBook() {
  try {
    const book = await fetchShippingBook()
    savedAddresses.value = book.addresses

    if (!hydrated.value) {
      if (book.pref.lastMemo) form.memo = book.pref.lastMemo

      const defaultSaved = book.addresses.find((item) => item.isDefault) || book.addresses[0]
      if (defaultSaved) {
        selectedAddressId.value = String(defaultSaved.idx)
        applyOption({
          id: String(defaultSaved.idx),
          label: defaultSaved.label,
          receiver: defaultSaved.receiver,
          phone: defaultSaved.phone,
          zipcode: defaultSaved.zipcode,
          address: defaultSaved.address,
          addressDetail: defaultSaved.addressDetail,
          isDefault: defaultSaved.isDefault,
        })
      } else if (orgOption.value) {
        selectedAddressId.value = ORG_ADDRESS_ID
        applyOption(orgOption.value)
      } else {
        startNewAddress()
      }
      hydrated.value = true
    }
  } catch {
    if (!hydrated.value && orgOption.value) {
      selectedAddressId.value = ORG_ADDRESS_ID
      applyOption(orgOption.value)
      hydrated.value = true
    }
  }
}

async function onSearchAddress() {
  try {
    const picked = await searchPostcode()
    if (!picked) return
    form.zipcode = picked.zipcode
    form.address = picked.extra ? `${picked.address} ${picked.extra}` : picked.address
    if (selectedAddressId.value === ORG_ADDRESS_ID) {
      selectedAddressId.value = NEW_ADDRESS_ID
      saveAddress.value = true
    } else if (isSavedSelection.value || selectedAddressId.value === NEW_ADDRESS_ID) {
      saveAddress.value = true
    }
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '주소 검색에 실패했습니다.'
  }
}

function validateShipping() {
  if (!form.receiverName || !form.receiverPhone || !form.address) {
    errorMessage.value = '받는 분, 연락처, 배송지를 모두 입력해 주세요.'
    return false
  }
  if (!isPhoneValid(form.receiverPhone)) {
    errorMessage.value = '휴대폰 번호를 올바르게 입력해 주세요.'
    return false
  }
  if (form.email && !isEmailValid(form.email)) {
    errorMessage.value = '이메일 형식을 확인해 주세요.'
    return false
  }
  return true
}

function validateForm() {
  return validateShipping()
}

async function persistShippingAndContact() {
  const shouldWriteAddress =
    saveAddress.value || (setAsDefault.value && isSavedSelection.value)
  if (shouldWriteAddress) {
    const payload = {
      label: form.label || '기본 배송지',
      receiver: form.receiverName,
      phone: form.receiverPhone,
      zipcode: form.zipcode,
      address: form.address,
      addressDetail: form.addressDetail,
      isDefault: setAsDefault.value || savedAddresses.value.length === 0,
    }
    if (isSavedSelection.value) {
      await updateShippingAddress(Number(selectedAddressId.value), payload)
    } else if (saveAddress.value) {
      const created = await createShippingAddress(payload)
      if (created?.idx) {
        selectedAddressId.value = String(created.idx)
      }
    }
  }

  if (saveMemo.value) {
    await saveShippingMemo(form.memo)
  }

  const updated = await updateMemberContact({
    phone: form.receiverPhone,
    email: form.email,
  })
  if (updated) {
    setMember(updated)
  }
}

const goToPayment = async () => {
  if (checkoutEmpty.value || submitting.value) return
  errorMessage.value = ''
  if (!validateShipping()) return

  submitting.value = true
  try {
    await persistShippingAndContact()
    await loadShippingBook()
    step.value = 'payment'
    window.scrollTo({ top: 0, behavior: 'smooth' })
    if (tossEnabled.value) {
      await nextTick()
      await ensureTossWidget()
    }
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '배송지를 저장하지 못했습니다.'
  } finally {
    submitting.value = false
  }
}

async function ensureTossWidget() {
  if (tossReady.value) return
  await mountToss(
    { methods: '#toss-payment-method', agreement: '#toss-agreement' },
    orderPayable.value,
  )
}

function orderNameOfLines() {
  const first = orderLines.value[0]?.product.name || '함우리몰 상품'
  const extra = orderLines.value.length - 1
  return extra > 0 ? `${first} 외 ${extra}건` : first
}

const onSubmit = async () => {
  if (step.value !== 'payment') return
  if (checkoutEmpty.value || submitting.value) return
  if (!tossEnabled.value) {
    errorMessage.value = '결제창을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.'
    return
  }
  errorMessage.value = ''
  if (!validateForm()) return

  if (orderLines.value.some((line) => line.soldOut || line.stockShort)) {
    errorMessage.value = '품절·재고 부족 상품이 포함되어 있습니다. 장바구니에서 확인해 주세요.'
    return
  }

  if (
    !(await confirmAction(`${formatPrice(orderPayable.value)}원을 결제할까요?`, {
      title: '결제 확인',
      confirmLabel: '결제하기',
      tone: 'primary',
    }))
  ) {
    return
  }

  submitting.value = true
  errorMessage.value = ''
  let createdOrder: { code: string } | null = null

  try {
    const { order } = await createMallOrder({
      receiver: form.receiverName,
      phone: form.receiverPhone,
      email: form.email,
      zipcode: form.zipcode,
      address: form.address,
      addressDetail: form.addressDetail,
      memo: form.memo,
      payMethod: 'TOSS',
      shippingFee: orderShipping.value,
      items: orderLines.value.map((line) => ({
        productId: line.productId,
        qty: line.qty,
        optionName: line.optionName,
        optionExtra: line.optionExtra,
        name: line.product.name,
        price: line.unitPrice,
        image: line.product.image,
        unit: line.product.unit,
      })),
    })

    /* 서버가 주문분만 카트에서 제거한다. 화면 상태만 맞춘다. */
    createdOrder = order
    const orderName = orderNameOfLines().slice(0, 100)
    placedOrder.value = order
    clearBuyNowKeys()
    clearSelection()
    await reload()

    await ensureTossWidget()
    await requestTossPay({
      orderId: order.code,
      orderName,
      amount: order.totalAmount,
      customerName: form.receiverName,
      customerEmail: form.email,
    })
  } catch (error) {
    if (createdOrder) {
      await router.replace({
        path: `/mall/orders/${createdOrder.code}`,
        query: { pay: 'closed' },
      })
      return
    }
    errorMessage.value = error instanceof Error ? error.message : '주문에 실패했습니다.'
  } finally {
    submitting.value = false
  }
}

watch(member, async () => {
  fillMemberDefaults(false)
  if (!hydrated.value) await loadShippingBook()
})

watch(
  () => [route.query.mode, lines.value.map((line) => line.key).join('|')] as const,
  () => {
    syncBuyNowSelection()
  },
  { immediate: true },
)

onMounted(async () => {
  syncBuyNowSelection()
  fillMemberDefaults(true)
  try {
    tossEnabled.value = await fetchTossAccess()
  } catch {
    tossEnabled.value = false
  }
  try {
    const orders = await fetchMyOrders()
    pendingOrder.value =
      orders.find((order) => order.status === 'PENDING' && !order.payNotifiedAt) ??
      orders.find((order) => order.status === 'PENDING') ??
      null
  } catch {
    pendingOrder.value = null
  }
  await loadShippingBook()
})

</script>

<style lang="scss" src="./MallCheckoutPage.scss"></style>
